import { FC, useContext, useEffect, useRef, useState } from 'react';
import * as ApiModels from '@/tsClient/servers/noodleDocuments/document-api-models';
import * as format from '@format';
import FileType from '@components/Icons/FileType';
import useNoodleApi from '@hooks/useNoodleApi';
import updateDocumentById from '@/tsClient/documents/updateDocumentById';
import { DocumentAccessContext } from '@/providers/DocumentAccess';
import { useToast } from '@/hooks';
import classNames from 'classnames';
import * as tsClient from '@tsClient';
import getLabelFromFieldName from '@helpers/getLabelFromFieldName';
import { CustomTermsDynamicVariableType } from '@typings/api-models';
import { useOpenCustomTermsPane } from '@panes/hooks';
import { useQueryState } from 'nuqs';
import { queryParam,queryStateParser } from '@/panes/queryStateParser';

import AdditionalTermsInfo from '@components/AdditionalTermsInfo';
import s from './File.module.scss';
import Button from '../Button';

type Props = {
  document: Pick<ApiModels.Document, 'id' | 'fileName' | 'createdAt' | 'uploadedBy' | 'mimeType' | 'uploadedAt'>;
  onDownload?: () => void;
  onReject?: () => void;
  onClose?: () => void;
  onReUpload?: () => void;
  onOpenAiReader?: () => void;
  onOpenExtractedData?: () => void;
  allowRename?: boolean;
  onRename?: () => void;
  showCorrection?: boolean;
};

const File: FC<Props> = ({
  document,
  onDownload,
  onReUpload,
  onReject,
  onClose,
  onOpenAiReader,
  allowRename,
  onRename,
  showCorrection = true,
  onOpenExtractedData,
}) => {
  const [name, setName] = useState(document.fileName);
  const [originalName] = useState(document.fileName);
  const [isCustomTerms, setIsCustomTerms] = useState(false);
  const [isCustomTermCorrectionModal, setCustomTermCorrectionModal] = useState(false);
  const labelRef = useRef<HTMLParagraphElement>(null);
  const { accessToken } = useContext(DocumentAccessContext);
  const openCustomTermsPane = useOpenCustomTermsPane();
  const [_queryState, setQueryState] = useQueryState(queryParam, queryStateParser);

  const { getData: sendReq } = useNoodleApi(updateDocumentById, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'File renamed successfully!'],
  });
  const { getData: generateCustomTerms } = useNoodleApi(tsClient.customTerms.createCustomTermsFromInstance);
  const { data: customTermsData, getData: getCustomTerms } = useNoodleApi(tsClient.customTerms.getCustomTerms, {
    toastOnError: true,
  });

  const getFileIdFromName = (fileName: string | null): string | null => {
    if (!fileName) return null;
    const match = fileName.match(/-([^.]+)\.pdf$/);
    return match ? match[1] : null;
  };

  useEffect(() => {
    const {fileName} = document;
    const isCustomTermsCheck = fileName.startsWith('customTerms');
    const fileId = getFileIdFromName(fileName);
    setIsCustomTerms(isCustomTermsCheck);
    if(isCustomTermsCheck) {
      const fetchData = async () : Promise<void> => {
        if (fileId) {
          await getCustomTerms({ id: fileId });
        }
      };
      fetchData();
    }
  }, [document]); // eslint-disable-line react-hooks/exhaustive-deps

  const customTermsDynamicVariables = customTermsData && customTermsData?.template.customTermsDynamicVariables
    .filter((cdv) => cdv.type === CustomTermsDynamicVariableType.Info)
    .map(variable => ({
      key: variable.name,
      label: getLabelFromFieldName(variable.name),
      type: variable.type,
    })) || [];

  const saveRename = async (): Promise<void> => {
    if (name.trim() === '' || name === originalName) {
      setName(originalName);
    } else {

      try {
        await sendReq({
          accessToken: accessToken || '',
          body: { fileName: name },
          documentId: document.id,
        });
        if (onRename) {
          onRename();
        }
      } catch (error) {
        setName(originalName);
      }
    }
  };

  const handleInput = (event: React.FormEvent<HTMLParagraphElement>): void => {
    const { currentTarget } = event;

    const selection = window.getSelection();
    const caretPosition = selection?.getRangeAt(0).startOffset || 0;

    setName(currentTarget.textContent || '');

    window.requestAnimationFrame(() => {
      selection?.collapse(currentTarget.firstChild, caretPosition);
    });
  };

  const handleSubmitAdditionalFields = async (data: Record<string, string | number | null | undefined>): Promise<void> => {
    const fileId = getFileIdFromName(document.fileName);
    if (fileId) {
      await generateCustomTerms({
        data,
        id: fileId,
      });
      await getCustomTerms({ id: fileId });
      setCustomTermCorrectionModal(false);
      setQueryState(null);
      openCustomTermsPane({ customTermsId: fileId });
    }
  };

  return (
    <div className={s.container}>
      <div className={s.document}>
        <FileType size={20} mimeType={document.mimeType} isEmpty={false} />
        <div className={s.infoText}>
          <p
            className={classNames(s.fileName, allowRename && s.fileNameEditable)}
            ref={labelRef}
            contentEditable={allowRename}
            suppressContentEditableWarning
            spellCheck={false}
            onBlur={saveRename}
            onClick={(e) => {
              if (allowRename) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            onInput={handleInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                labelRef.current?.blur();
              }
            }}
          >
            {name}
          </p>
          {(document.uploadedBy.name || document.createdAt) && <p className={s.uploadedBy}>
            <span>{document.uploadedBy.name}</span> {document.createdAt && <>{format.datetimeLocalTZ.timeSince({ date: document.createdAt })} ago</>}
          </p>}
        </div>
        <div className={s.buttons}>
          {onOpenExtractedData && (
            <Button onClick={onOpenExtractedData} size="sm" variant="secondary">
              View Extracted Data
            </Button>
          )}
          {onOpenAiReader && document.mimeType.includes('image') && (
            <Button onClick={onOpenAiReader} size="sm" variant="secondary">
              Open AI Reader
            </Button>
          )}
          {onReUpload && (
            <Button onClick={onReUpload} size="sm" variant="primary">
              Re-Upload
            </Button>
          )}
          {onDownload && (
            <Button onClick={onDownload} size="sm" variant="secondary">
              Download
            </Button>
          )}
          {isCustomTerms && showCorrection && (
            <Button onClick={() => setCustomTermCorrectionModal(true)} size="sm" variant="destructive">
              Make a correction
            </Button>
          )}
          {onReject && (
            <Button onClick={onReject} size="sm" variant="destructive">
              Reject
            </Button>
          )}
          {onClose && (
            <Button onClick={onClose} size="sm" variant="secondary">
              Close
            </Button>
          )}
          {isCustomTermCorrectionModal && (
            <div
              className={s.modalOverlay}
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setCustomTermCorrectionModal(false);
                }
              }}
            >
              <div className={s.modalContainer}>
                <div className={s.modalHeader}>
                  <button
                    className={s.closeButton}
                    onClick={() => { setCustomTermCorrectionModal(false); }}
                  >
                    ×
                  </button>
                </div>
                <AdditionalTermsInfo
                  initialValues={customTermsData?.contextVariables}
                  additionalFields={customTermsDynamicVariables}
                  onSubmit={handleSubmitAdditionalFields}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default File;
